import React, { createContext, useContext } from 'react';
// Disabled 2026-08-27 — see the bypass block below. Left imported/commented rather than deleted
// so re-enabling is a revert, not a rewrite: useMeMenu, useMePermissions, permissionsForMenuPath,
// scopeCovers, ForbiddenPage and PermissionLoadingPage are the pieces the original body used.
// import { useMemo, useRef } from 'react';
// import useMeMenu from '../../hooks/useMeMenu';
// import useMePermissions from '../../hooks/useMePermissions';
// import { permissionsForMenuPath, scopeCovers } from '../../services/permissions';
// import { ForbiddenPage, PermissionLoadingPage } from '../guards/ForbiddenPage';
import type {
  PermissionPrincipalSource,
  RbacDataScope,
  RbacEffectiveGrant,
  RbacMeGroup,
  RbacMePrincipal,
  RbacPermissionAction,
  RbacPrimaryRole,
} from '../../types/permissions';

export interface PermissionContextValue {
  enabled: boolean;
  isBypassed: boolean;
  isReady: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  mode: string;
  version: number;
  tenant: string;
  principal: RbacMePrincipal | null;
  primaryRole: RbacPrimaryRole | null;
  groups: RbacMeGroup[];
  scopes: Record<string, unknown>;
  permissions: string[];
  permissionMap: Record<string, RbacEffectiveGrant>;
  /** True when at least one code grants view. Kept for existing category guards. */
  can: (...required: string[]) => boolean;
  canAll: (...required: string[]) => boolean;
  canAction: (
    permission: string,
    action?: RbacPermissionAction,
    requiredScope?: RbacDataScope
  ) => boolean;
  canAccessPath: (
    path: string,
    action?: RbacPermissionAction,
    requiredScope?: RbacDataScope
  ) => boolean;
  scopeFor: (
    permission: string,
    action?: RbacPermissionAction
  ) => RbacDataScope | null;
  refresh: () => Promise<void>;
}

const deny = () => false;
const deniedRefresh = async () => undefined;

const DENIED_CONTEXT: PermissionContextValue = {
  enabled: true,
  isBypassed: false,
  isReady: false,
  isLoading: false,
  isFetching: false,
  isError: true,
  error: new Error('RBAC_PERMISSION_PROVIDER_MISSING'),
  mode: 'enforce',
  version: 0,
  tenant: '',
  principal: null,
  primaryRole: null,
  groups: [],
  scopes: {},
  permissions: [],
  permissionMap: {},
  can: deny,
  canAll: deny,
  canAction: deny,
  canAccessPath: deny,
  scopeFor: () => null,
  refresh: deniedRefresh,
};

const PermissionContext = createContext<PermissionContextValue | null>(null);

export interface PermissionProviderProps {
  site?: string | null;
  user?: PermissionPrincipalSource | null;
  children: React.ReactNode;
}

// DISABLED 2026-08-27 — RBAC is not something we will use in the near future. Kept commented,
// not deleted, because this whole block is what re-enabling it means restoring.
//
// The feature assumes `sso.portal-mode:unified` (RequireSsoPortalMode on P1's
// `api/v1/portal/{site_type}/*` routes); dev3's tenants — medix AND hitowa — are both
// `portal_mode = 'legacy_split'`. Both `/me/permissions` and `/me/menu` therefore 404 on every
// hitowa clinic login (measured on dev3 2026-08-27: `RahShell` mounts `PermissionProvider`
// unconditionally by tenant identity — `getTenantCustomization(siteType)` — with no portal-mode
// check of its own), and the OLD body below fails closed on any query error, including this one.
// That produced the generic fallback message (`権限情報を取得できませんでした…`) for every single
// hitowa clinic user, unconditionally — not a per-account problem.
//
// const primaryRoleFrom = (payload: MePermissions): RbacPrimaryRole | null =>
//   payload.principal?.primaryRole ??
//   payload.groups.find(group => group.isPrimary)?.role ??
//   null;
//
// const permissionErrorMessage = (error: unknown): string => {
//   const status = (error as { response?: { status?: number } })?.response?.status;
//   if (status === 403) {
//     return 'このアカウントには HITOWA の利用権限が割り当てられていません。';
//   }
//   if (status === 401) {
//     return 'ログイン状態を確認できませんでした。再度ログインしてください。';
//   }
//   return '権限情報を取得できませんでした。通信状態を確認して再度お試しください。';
// };
//
// /**
//  * Loads effective permissions independently from login/SSO payloads.
//  *
//  * Children are withheld while the first request is pending and on every
//  * terminal error. This makes loading, malformed responses, and network errors
//  * fail closed; only an explicit backend `enabled:false` result bypasses checks.
//  */
// export const PermissionProvider: React.FC<PermissionProviderProps> = ({
//   site,
//   user,
//   children,
// }) => {
//   const query = useMePermissions({ site, user });
//   const menuQuery = useMeMenu({ site, user });
//   const { data, isFetching, refetch } = query;
//   const {
//     data: menuData,
//     refetch: refetchMenu,
//     isFetching: isMenuFetching,
//   } = menuQuery;
//   const boundaryKey = `${site ?? ''}:${user?.id ?? 'unknown'}:${query.principalType}`;
//   const lastAligned = useRef<{
//     boundaryKey: string;
//     value: PermissionContextValue;
//   } | null>(null);
//   const versionsMismatch = Boolean(
//     data &&
//       menuData &&
//       (menuData.enabled !== data.enabled ||
//         (data.enabled && menuData.rbacVersion !== data.version))
//   );
//
//   const value = useMemo<PermissionContextValue | null>(() => {
//     const payload = data;
//     if (!payload) return null;
//
//     const isBypassed = payload.enabled === false;
//     const menu = menuData;
//     if (!menu) return null;
//     if (
//       menu.enabled !== payload.enabled ||
//       (payload.enabled && menu.rbacVersion !== payload.version)
//     ) {
//       return null;
//     }
//     const canAction = (
//       permission: string,
//       action: RbacPermissionAction = 'view',
//       requiredScope?: RbacDataScope
//     ): boolean => {
//       if (isBypassed) return true;
//       const grant = payload.permissions[permission.trim()];
//       return Boolean(
//         grant?.actions[action] && scopeCovers(grant.scopes[action], requiredScope)
//       );
//     };
//
//     return {
//       enabled: payload.enabled,
//       isBypassed,
//       isReady: true,
//       isLoading: false,
//       isFetching: isFetching || isMenuFetching,
//       isError: false,
//       error: null,
//       mode: payload.mode,
//       version: payload.version,
//       tenant: payload.tenant,
//       principal: payload.principal,
//       primaryRole: primaryRoleFrom(payload),
//       groups: payload.groups,
//       scopes: payload.scopes,
//       permissions: payload.visiblePermissionCodes,
//       permissionMap: payload.permissions,
//       can: (...required: string[]) =>
//         required.length === 0 || required.some(permission => canAction(permission, 'view')),
//       canAll: (...required: string[]) =>
//         required.length === 0 || required.every(permission => canAction(permission, 'view')),
//       canAction,
//       canAccessPath: (
//         path: string,
//         action: RbacPermissionAction = 'view',
//         requiredScope?: RbacDataScope
//       ) => {
//         if (isBypassed) return true;
//         const requiredPermissions = permissionsForMenuPath(menu, path);
//         if (requiredPermissions === null) return false;
//         return (
//           requiredPermissions.length === 0 ||
//           requiredPermissions.some(permission =>
//             canAction(permission, action, requiredScope)
//           )
//         );
//       },
//       scopeFor: (permission: string, action: RbacPermissionAction = 'view') =>
//         isBypassed
//           ? 'all'
//           : payload.permissions[permission.trim()]?.scopes[action] ?? null,
//       refresh: async () => {
//         await Promise.all([refetch(), refetchMenu()]);
//       },
//     };
//   }, [data, isFetching, isMenuFetching, menuData, refetch, refetchMenu]);
//
//   if (value !== null) {
//     lastAligned.current = { boundaryKey, value };
//   }
//
//   const retainedValue =
//     value ??
//     (versionsMismatch &&
//     (query.isFetching || menuQuery.isFetching) &&
//     lastAligned.current?.boundaryKey === boundaryKey
//       ? { ...lastAligned.current.value, isFetching: true }
//       : null);
//
//   if (!query.queryEnabled || !menuQuery.queryEnabled) {
//     return (
//       <ForbiddenPage message="権限を確認するためのユーザー情報またはテナント情報がありません。" />
//     );
//   }
//
//   if (
//     query.isPending ||
//     menuQuery.isPending ||
//     (versionsMismatch && (query.isFetching || isMenuFetching) && retainedValue === null)
//   ) {
//     return <PermissionLoadingPage />;
//   }
//
//   if (query.isError || menuQuery.isError || retainedValue === null) {
//     return (
//       <ForbiddenPage
//         message={permissionErrorMessage(query.error ?? menuQuery.error)}
//         onRetry={() => {
//           void Promise.all([query.refetch(), refetchMenu()]);
//         }}
//         isRetrying={query.isFetching || isMenuFetching}
//       />
//     );
//   }
//
//   return (
//     <PermissionContext.Provider value={retainedValue}>
//       {children}
//     </PermissionContext.Provider>
//   );
// };

/**
 * RBAC is off. Renders children immediately — no `/me/permissions` or `/me/menu` call, no
 * loading state, no error boundary — with a context value matching what the code above already
 * treats as "bypassed" (`payload.enabled === false`): every `can*` check grants, `scopeFor`
 * answers `'all'`. Same shape every `Can`/`RequirePermission`/`usePermissions()` consumer already
 * expects, so nothing downstream needed to change.
 */
export const PermissionProvider: React.FC<PermissionProviderProps> = ({ site, children }) => {
  const value: PermissionContextValue = {
    enabled: false,
    isBypassed: true,
    isReady: true,
    isLoading: false,
    isFetching: false,
    isError: false,
    error: null,
    mode: 'disabled',
    version: 0,
    tenant: site ?? '',
    principal: null,
    primaryRole: null,
    groups: [],
    scopes: {},
    permissions: [],
    permissionMap: {},
    can: () => true,
    canAll: () => true,
    canAction: () => true,
    canAccessPath: () => true,
    scopeFor: () => 'all',
    refresh: async () => undefined,
  };

  return (
    <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>
  );
};

/** Outside the provider every check denies; there is no storage fallback. */
export function usePermissions(): PermissionContextValue {
  return useContext(PermissionContext) ?? DENIED_CONTEXT;
}

export default PermissionProvider;
